import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Radio,
  Select,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuilding,
  IconMapPin,
  IconSearch,
  IconStarFilled,
} from '@tabler/icons-react';
import { bestPriceService } from '@/api/services/BestPriceService';
import { orderService } from '@/api/services/OrderService';
import { isServiceUnavailable } from '@/api/errors';
import { useAgencyAgreementMissed, useSaleTree } from '@/hooks/useSell';
import { flattenSaleTree, groupSellByBasis } from '@/lib/sellTree';
import { formatMoney, formatOrgName, formatPropertyCondition, formatQualityValue } from '@/lib/format';
import { useAuth } from '@/stores/authContext';
import { ROUTES } from '@/routes/paths';
import BasisPicker from '@/components/common/BasisPicker';
import type { BasisValue } from '@/lib/basisPicker';
import { useIsAgent } from '@/hooks/useRoles';
import { useAttestationRequired } from '@/hooks/useAttestationRequired';
import AgencyGateBanner from '@/components/app/sell/AgencyGateBanner';
import SellAttestationGate from '@/components/app/sell/SellAttestationGate';
import BestPriceLink from '@/components/common/BestPriceLink';
import type { SellBestPrice } from '@/types/sell';
import IdText from '@/components/common/IdText';

const FETCH_LIMIT = 20;
const PAGE_SIZE = 5;

// Тип предоплаты, давший лучшую FCA-цену (с бэка) → короткая подпись для бейджа карточки.
const PREPAYMENT_LABELS: Readonly<Record<string, string>> = {
  PERCENT_0: 'app.sell.prepayment0Short',
  PERCENT_100: 'app.sell.prepayment100Short',
};

interface SearchParams {
  readonly address: string;
  readonly addressTitle: string;
  readonly goodsType: string;
  readonly propertySetId: number;
  readonly prepaymentTypes: string[];
}

// Ссылка на сделку «Продать» (свободный режим, без заявки) из карточки поиска: продавец заполнит
// логистику на странице /app/sell/counter. Базис/предоплата — из критериев выполненного поиска.
function sellDealUrl(item: SellBestPrice, address: string, addressTitle: string, prepaymentType: string): string {
  const qs = new URLSearchParams({
    purchaseOrderId: String(item.purchaseOrderId),
    purchaseOrderVersion: String(item.purchaseOrderVersion),
    fromAddress: address,
    fromAddressTitle: addressTitle,
    prepayment: prepaymentType === 'PERCENT_100' ? 'PERCENT_100' : 'PERCENT_0',
  });
  if (item.cropYear != null) {
    qs.set('year', String(item.cropYear));
  }
  return `${ROUTES.APP_SELL_COUNTER}?${qs.toString()}`;
}

function BuyerRow({
  item,
  best,
  dealTo,
  isAgent,
}: {
  readonly item: SellBestPrice;
  readonly best: boolean;
  readonly dealTo: string;
  /** Агент пользуется поиском справочно — действие «Продать по X» ему не показываем. */
  readonly isAgent: boolean;
}) {
  const { t } = useTranslation();
  return (
    <Card
      withBorder
      padding="lg"
      bg={best ? 'lime.0' : undefined}
      style={best ? { borderColor: 'var(--mantine-color-brand-4)' } : undefined}
    >
      <Stack gap="sm">
        {best ? (
          <Group gap={6}>
            <IconStarFilled size={16} color="var(--mantine-color-wheat-6)" />
            <Text fw={700} c="brand.9">
              {t('app.sell.search.bestOffer')}
            </Text>
          </Group>
        ) : null}
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
            <Group gap={6} wrap="nowrap">
              <IconBuilding size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text fw={600} style={{ minWidth: 0 }}>
                {formatOrgName(item.buyerName) ?? '—'}
              </Text>
              {item.buyerInn ? (
                <Text size="sm" c="gray.7" style={{ flexShrink: 0 }}>
                  · {t('auth.fields.inn')} <IdText>{item.buyerInn}</IdText>
                </Text>
              ) : null}
            </Group>
            <Group gap={6} wrap="nowrap">
              <IconMapPin size={16} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
              <Text size="sm" c="gray.8">
                {formatOrgName(item.buyerAddressTitle) ?? item.buyerAddress ?? '—'}
              </Text>
            </Group>
            {item.propertyName || item.prepaymentType ? (
              <Group gap={4} wrap="wrap">
                {item.propertyName ? (
                  <Badge variant="default" tt="none" fw={500} radius="sm" c="gray.9">
                    {item.propertyName}
                    {item.propertyValue != null ? ` ${formatQualityValue(item.propertyValue)}` : ''}
                    {item.propertyPostfix ?? ''}
                  </Badge>
                ) : null}
                {item.prepaymentType && PREPAYMENT_LABELS[item.prepaymentType] ? (
                  <Badge variant="light" color="brand" tt="none" fw={500} radius="sm">
                    {t(PREPAYMENT_LABELS[item.prepaymentType]!)}
                  </Badge>
                ) : null}
              </Group>
            ) : null}
          </Stack>
          <Group gap={6} align="baseline">
            <Text fz={best ? 28 : 22} fw={700} c="brand.9">
              {formatMoney(item.pricePerTon)}
            </Text>
            <Text size="sm" c="gray.8">
              {t('app.sell.rubPerTon')}
            </Text>
          </Group>
        </Group>
        {!isAgent ? (
          <Group justify="flex-end">
            <BestPriceLink to={dealTo} price={item.pricePerTon} action="sell" tone={best ? 'gain' : 'plain'} />
          </Group>
        ) : null}
      </Stack>
    </Card>
  );
}

// «Лучшая цена» раздела «Продам»: лучшие цены ПОКУПКИ под мой товар с отгрузкой с моего базиса
// (зеркало Buy best-price). Под гейтом агентского договора. Качество — основной параметр культуры.
export default function SellBestPriceSearchPage() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const isAgent = useIsAgent(); // агенту в результатах не показываем CTA «Продать по X» (только справочно)
  const agencyMissed = useAgencyAgreementMissed();
  // Агента аттестация не касается (он не аттестуется и пользуется поиском справочно) — исключаем его,
  // как и в остальных гейтах платформы. Иначе агент с isVerified=false/undefined был бы заблокирован.
  const attestationBlocked = useAttestationRequired() && !isAgent;

  const [basis, setBasis] = useState<BasisValue | null>(null);
  const [goodsType, setGoodsType] = useState<string | null>(null);
  const [propertySetId, setPropertySetId] = useState<string | null>(null);
  const [prepayment, setPrepayment] = useState<string>('ANY');
  const [submitted, setSubmitted] = useState<SearchParams | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Мои базисы (точки погрузки) — из дерева продаж. В пикере — группа «Мои базисы» + поиск по справочнику.
  const tree = useSaleTree();
  const myBases = useMemo(() => groupSellByBasis(flattenSaleTree(tree.data)), [tree.data]);
  const ownBases = useMemo(
    () => myBases.map((base) => ({ address: base.address, addressTitle: base.addressTitle })),
    [myBases],
  );

  const goodsClasses = useQuery({
    queryKey: ['goodsClasses'] as const,
    queryFn: ({ signal }) => orderService.getGoodsClasses(signal),
    enabled: isAuthenticated && !agencyMissed && !attestationBlocked,
    staleTime: 10 * 60_000,
  });

  const propertySets = useQuery({
    queryKey: ['goodsPropertySets', goodsType] as const,
    queryFn: ({ signal }) => orderService.getGoodsPropertySets(goodsType!, signal),
    enabled: isAuthenticated && !!goodsType,
    staleTime: 10 * 60_000,
  });

  const results = useQuery({
    queryKey: ['sellBestPriceSearch', submitted] as const,
    queryFn: ({ signal }) =>
      bestPriceService.getSellForAddress(
        {
          address: submitted!.address,
          goodsType: submitted!.goodsType,
          propertySetId: submitted!.propertySetId,
          prepaymentTypes: submitted!.prepaymentTypes,
          limit: FETCH_LIMIT,
          offset: 0,
        },
        signal,
      ),
    enabled: isAuthenticated && !!submitted,
    staleTime: 30_000,
    retry: false,
  });

  const mainPropertyName = propertySets.data?.[0]?.properties?.[0]?.name;
  const propertyData = useMemo(
    () =>
      (propertySets.data ?? [])
        .filter((set) => set.propsSetId != null && set.properties?.[0]?.value != null)
        .map((set) => {
          const property = set.properties![0]!;
          return { value: String(set.propsSetId), label: formatPropertyCondition(property) };
        }),
    [propertySets.data],
  );

  const canSearch = !!basis && !!goodsType && !!propertySetId;

  const search = (): void => {
    if (!canSearch) {
      return;
    }
    setVisibleCount(PAGE_SIZE);
    setSubmitted({
      address: basis!.address,
      addressTitle: basis!.addressTitle,
      goodsType: goodsType!,
      propertySetId: Number(propertySetId),
      prepaymentTypes: prepayment === 'ANY' ? ['PERCENT_0', 'PERCENT_100'] : [prepayment],
    });
  };

  const items = results.data ?? [];
  const best = items[0];
  const rest = items.slice(1, 1 + visibleCount);
  const hasMore = items.length > 1 + visibleCount;

  // Предоплата для сделки: если искали конкретную — её, иначе (ANY) — 0% по умолчанию.
  const dealPrepayment = submitted?.prepaymentTypes.length === 1 ? submitted.prepaymentTypes[0]! : 'PERCENT_0';
  const dealTo = (item: SellBestPrice): string =>
    submitted ? sellDealUrl(item, submitted.address, submitted.addressTitle, dealPrepayment) : ROUTES.APP_SELL;

  return (
    <Container size="md" py="xl">
      <Stack gap="lg">
        <Stack gap="xs">
          <Anchor component={Link} to={ROUTES.APP_SELL} size="sm" c="brand.7">
            <Group gap={4} wrap="nowrap">
              <IconArrowLeft size={16} />
              {t('app.sell.search.toOffers')}
            </Group>
          </Anchor>
          <Group gap="sm" wrap="nowrap" align="flex-start">
            <ThemeIcon variant="light" color="brand" size={44} radius="md" style={{ flexShrink: 0 }}>
              <IconSearch size={26} />
            </ThemeIcon>
            <Stack gap={4}>
              <Title order={2}>{t('app.sell.search.title')}</Title>
              <Text c="gray.8">{t('app.sell.search.subtitle')}</Text>
            </Stack>
          </Group>
        </Stack>

        {/* Аттестация — базовое требование, проверяем её раньше агентского договора (единый стиль
            с закупочной «Лучшей ценой» и запретом создания предложений). */}
        {attestationBlocked ? (
          <SellAttestationGate />
        ) : agencyMissed ? (
          <AgencyGateBanner />
        ) : (
          <>
            <Card withBorder padding="lg">
              <Stack gap="md">
                <Group grow align="flex-start" preventGrowOverflow={false} wrap="wrap">
                  <BasisPicker
                    value={basis}
                    onChange={setBasis}
                    ownBases={ownBases}
                    label={t('app.sell.search.address')}
                    placeholder={t('app.sell.search.addressPlaceholder')}
                    miw={260}
                  />
                  <Select
                    label={t('app.sell.search.goods')}
                    placeholder={t('app.sell.search.goodsPlaceholder')}
                    data={goodsClasses.data ?? []}
                    value={goodsType}
                    onChange={(value) => {
                      setGoodsType(value);
                      setPropertySetId(null);
                    }}
                    searchable
                    miw={220}
                  />
                  <Select
                    label={mainPropertyName ?? t('app.sell.search.quality')}
                    placeholder={goodsType ? t('app.sell.search.qualityPlaceholder') : t('app.sell.search.qualityFirst')}
                    data={propertyData}
                    value={propertySetId}
                    onChange={setPropertySetId}
                    disabled={!goodsType || propertySets.isLoading}
                    miw={180}
                  />
                </Group>
                <Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
                  <Stack gap={4}>
                    <Text size="sm" fw={500}>
                      {t('app.sell.search.prepayment')}
                    </Text>
                    <Radio.Group value={prepayment} onChange={setPrepayment}>
                      <Group gap="lg" mt={6}>
                        <Radio value="ANY" label={t('app.sell.search.prepaymentAny')} />
                        <Radio value="PERCENT_0" label={t('app.sell.prepayment0Short')} />
                        <Radio value="PERCENT_100" label={t('app.sell.prepayment100Short')} />
                      </Group>
                    </Radio.Group>
                  </Stack>
                  <Button onClick={search} disabled={!canSearch} loading={results.isFetching} leftSection={<IconSearch size={18} />}>
                    {t('app.sell.search.find')}
                  </Button>
                </Group>
              </Stack>
            </Card>


            {!submitted ? (
              <Card withBorder padding="xl">
                <Stack gap={4} align="center">
                  <Text fw={600}>{t('app.sell.search.hintTitle')}</Text>
                  <Text c="gray.8" ta="center">
                    {t('app.sell.search.hintText')}
                  </Text>
                </Stack>
              </Card>
            ) : results.isError ? (
              <Alert color="red" variant="light" icon={<IconAlertTriangle size={18} />}>
                <Group justify="space-between" wrap="nowrap">
                  <Text>
                    {isServiceUnavailable(results.error) ? t('app.sell.search.unavailable') : t('app.sell.search.failed')}
                  </Text>
                  <Button variant="subtle" size="compact-sm" onClick={() => results.refetch()}>
                    {t('common.retry')}
                  </Button>
                </Group>
              </Alert>
            ) : results.isLoading ? (
              <Stack gap="sm">
                <Skeleton h={150} />
                <Skeleton h={110} />
                <Skeleton h={110} />
              </Stack>
            ) : items.length === 0 ? (
              <Card withBorder padding="xl">
                <Stack gap={4} align="center">
                  <Text fw={600}>{t('app.sell.search.emptyTitle')}</Text>
                  <Text c="gray.8" ta="center">
                    {t('app.sell.search.emptyText')}
                  </Text>
                </Stack>
              </Card>
            ) : (
              <Stack gap="sm">
                {best ? <BuyerRow item={best} best dealTo={dealTo(best)} isAgent={isAgent} /> : null}
                {rest.map((item) => (
                  <BuyerRow key={`${item.purchaseOrderId}-${item.pricePerTon}`} item={item} best={false} dealTo={dealTo(item)} isAgent={isAgent} />
                ))}
                {hasMore ? (
                  <Button variant="default" onClick={() => setVisibleCount((count) => count + PAGE_SIZE)} style={{ alignSelf: 'center' }}>
                    {t('app.sell.search.showMore')}
                  </Button>
                ) : null}
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
}
